import { RenderStorage } from '../../../core/render/RenderStorage';
import { RotatedBoundingBox } from '../../../data/common/RotatedBoundingBox';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { SDFOperationRenderer } from './BooleanRenderer';

/*import { FrameBufferObject } from '../../../core/utils/FrameBufferObject'
import { gl } from '../../../Engine'
import { QuadRenderer } from '../../../core/render/renderers/QuadRenderer'
import { BoundingBox } from '../../../data/common/BoundingBox'
import { SignedDistanceField } from '../../../core/models/SignedDistanceField'
import { ShaderProgram } from '../../../core/utils/ShaderProgram'
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix'
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat'
import { IntersectionSliceSource } from '../../../data/common/customs/CustomTargetObject'*/

export enum BooleanOperation {
    OR = 'or',
    AND = 'and',
    XOR = 'xor',
    AND_NOT = 'not',
}

//TODO: SDFOperations always assume that the margins are of the same size
/**
 * Class that contains methods for applying {@link BooleanOperation BooleanOperations} on {@link RegionOfInterest RegionsOfInterest}.\
 * These operations work entirely with {@link ContourTexture ContourTextures} and perform the {@link BooleanOperation BooleanOperations} on the {@link ContourTexture.signedDistanceField SignedDistanceFields} of the {@link ContourTexture ContourTextures}.\
 * To keep the {@link RegionOfInterest}'s data models and state management up-to-date,\
 * it is strongly recommended to apply these operations on the {@link RegionOfInterest} instead of {@link ContourTexture}.
 */
export class SDFOperations {
    public static applyBoolean(
        src1: ContourTexture,
        src2: ContourTexture,
        operation: BooleanOperation,
        destination: RegionOfInterest
    ) {
        switch (operation) {
            case BooleanOperation.OR:
                this.or(src1, src2, destination);
                return;
            case BooleanOperation.AND:
                this.and(src1, src2, destination);
                return;
            case BooleanOperation.AND_NOT:
                this.andNot(src1, src2, destination);
                return;
            case BooleanOperation.XOR:
                this.xor(src1, src2, destination);
                return;
            default:
                throw new Error(
                    `Attempt to apply unknown boolean operation ${operation}`
                );
        }
    }

    /**
     * Method to apply the {@link BooleanOperation.OR OR}-operation combining the contour data of {@link src1} and {@link src2}, storing the result in {@link destination}.\
     * {@link destination} will be scaled to cover the volume of {@link src1} and {@link src2} within the space defined by {@link destinationSpace}.
     * @param src1 The first {@link RegionOfInterest} or {@link ContourTexture} that will be used within the {@link BooleanOperation.OR OR}-operation.
     * @param src2 The second {@link RegionOfInterest} or {@link ContourTexture} that will be used within the {@link BooleanOperation.OR OR}-operation.
     * @param destination {@link RegionOfInterest} that will be scaled to cover the volume of {@link src1} and {@link src2} within the space defined by {@link destinationSpace}.\
     * Any old {@link ContourTexture texture data} within {@link destination} will be overwritten.\
     * {@link destination} may be equal to {@link src1} or {@link src2}.
     * @param destinationSpace The {@link SliceSource space} with which the {@link destination} {@link RegionOfInterest} should align with.
     * {@link destinationSpace} may be equal to {@link src1} or {@link src2}.\
     * But preferably it should be the {@link SliceSource} that covers the most {@link SliceData slices} within the destination space.
     * @returns True if the operation was successful and destination was changed as a result, false otherwise.\
     * This will return false if both source textures are empty. Or if {@link destination} is equal to {@link src1} or {@link src2} and the other source is empty.
     */
    public static or(
        src1: ContourTexture,
        src2: ContourTexture,
        destination: RegionOfInterest
    ) {
        //compute slices and rotation for destination
        const tex1 = src1;
        const tex2 = src2;
        if (tex1) {
            if (tex2) {
                if (destination.texture === src1) {
                    if (tex1.fullBoundingBox.contains(tex2.fullBoundingBox)) {
                        RenderStorage.getRenderer(
                            SDFOperationRenderer
                        ).renderOnDestination(
                            destination,
                            BooleanOperation.OR,
                            src2
                        );
                    } else {
                        //create temp and at the end copy that to destination
                        const bb = new RotatedBoundingBox(tex1.fullBoundingBox);
                        bb.rotate(tex1.fullBoundingBox.rotation);

                        bb.expandWithPoints(
                            tex2.fullBoundingBox.getVertices(bb.invRotation)
                        );
                        const temp = new ContourTexture(tex1.grid, bb);
                        RenderStorage.getRenderer(
                            SDFOperationRenderer
                        ).renderOnDestination(
                            temp,
                            BooleanOperation.OR,
                            src1,
                            src2
                        );

                        tex1.resetGeometry(temp);
                        RenderStorage.getRenderer(
                            SDFOperationRenderer
                        ).renderOnDestination(tex1, BooleanOperation.OR, temp);
                        temp.delete();
                    }
                    return true;
                } else if (destination.texture === src2) {
                    if (tex2.fullBoundingBox.contains(tex1.fullBoundingBox)) {
                        //render sr1.texture on src2
                        RenderStorage.getRenderer(
                            SDFOperationRenderer
                        ).renderOnDestination(
                            destination,
                            BooleanOperation.OR,
                            src1
                        );
                    } else {
                        //create temp and at the end copy that to destination
                        const bb = new RotatedBoundingBox(
                            tex2.contentBoundingBox
                        );
                        bb.expandWithPoints(
                            tex1.contentBoundingBox.getVertices(bb.invRotation)
                        );
                        bb.addMargins(tex2.margin);
                        const temp = new ContourTexture(tex1.grid, bb);
                        RenderStorage.getRenderer(
                            SDFOperationRenderer
                        ).renderOnDestination(
                            temp,
                            BooleanOperation.OR,
                            src2,
                            src1
                        );
                        tex2.set(temp);
                        RenderStorage.getRenderer(
                            SDFOperationRenderer
                        ).renderOnDestination(tex2, BooleanOperation.OR, temp);
                        temp.delete();
                    }
                    return true;
                } else {
                    const bb = new RotatedBoundingBox(tex1.contentBoundingBox);
                    bb.expandWithPoints(
                        tex2.contentBoundingBox.getVertices(bb.invRotation)
                    );
                    bb.addMargins(tex1.margin);
                    destination.generateTextureSlices(bb);
                    RenderStorage.getRenderer(
                        SDFOperationRenderer
                    ).renderOnDestination(
                        destination,
                        BooleanOperation.OR,
                        src1,
                        src2
                    );
                    return true;
                }
            } else {
                if (destination.texture === src1) {
                    return false;
                } else {
                    //copy src1 to destination, note any old data is overwritten
                    destination.generateTextureSlices(tex1.fullBoundingBox);
                    RenderStorage.getRenderer(
                        SDFOperationRenderer
                    ).renderOnDestination(
                        destination,
                        BooleanOperation.OR,
                        src1
                    );
                    return true;
                }
            }
        } else if (tex2) {
            if (destination.texture === src2) {
                return false;
            } else {
                //copy src2 to destination, note any old data is overwritten
                destination.generateTextureSlices(tex2.fullBoundingBox);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(destination, BooleanOperation.OR, src2);
                return true;
            }
        }
    }

    /**
     * Method to apply the {@link BooleanOperation.AND AND}-operation, which gets the shared volume in between {@link src1} and {@link src2}, storing the result in {@link destination}.\
     * {@link destination} will be scaled to cover the volume of the {@link RotatedBoundingBox.rotatedUnion union} in between {@link src1} and {@link src2} within the space defined by {@link destinationSpace}.\
     * If {@link src1} and {@link src2} do not overlap, {@link destination} will be cleared and false will be returned.
     * @param src1 The first {@link ContourTexture} or {@link RegionOfInterest} that will be used within the {@link BooleanOperation.AND AND}-operation.
     * @param src2 The second {@link ContourTexture} or {@link RegionOfInterest} that will be used within the {@link BooleanOperation.AND AND}-operation.
     * @param destination {@link RegionOfInterest} that will be scaled to cover the shared volume between {@link src1} and {@link src2} within the space defined by {@link destinationSpace}.\
     * Any old {@link ContourTexture texture data} within {@link destination} will be overwritten.\
     * {@link destination} may be equal to {@link src1} or {@link src2}.
     * @param destinationSpace @param destinationSpace The {@link SliceSource space} with which the {@link destination} {@link RegionOfInterest} should align with.
     * {@link destinationSpace} may be equal to {@link src1} or {@link src2}.\
     * But preferably it should be the {@link SliceSource} that covers the most {@link SliceData slices} within the destination space.
     * @returns False if {@link src1} and {@link src2} do not overlap, in which case the result is empty texture, true otherwise.
     */
    public static and(
        src1: ContourTexture,
        src2: ContourTexture,
        destination: RegionOfInterest
    ): boolean {
        const tex1 = src1;
        if (!tex1) {
            throw new Error(
                `Attempt to perform SDF boolean operation 'and', while src1 was empty texture`
            );
        }
        const tex2 = src2;
        if (!tex2) {
            throw new Error(
                `Attempt to perform SDF boolean operation 'and', while src2 was empty texture`
            );
        }
        const rotation = destination._imageGrid.getOrientation();
        const overlap = RotatedBoundingBox.rotatedUnion(
            rotation,
            tex1.contentBoundingBox,
            tex2.contentBoundingBox
        );
        if (!overlap) {
            return false;
        }
        overlap.addMargins(
            destination.texture
                ? destination.texture.margin
                : ContourTexture.DEFAULT_MARGIN
        );
        if (src1 === destination.texture) {
            //we create a temp texture so we get more optimized texture bounding box.
            const temp = new ContourTexture(tex1.grid, overlap);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                temp,
                BooleanOperation.OR,
                src1
            );
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                temp,
                BooleanOperation.AND,
                src2
            );
            tex1.set(temp);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                tex1,
                BooleanOperation.OR,
                temp
            );
            temp.delete();
        } else if (src2 === destination.texture) {
            //we create a temp texture so we get more optimized texture bounding box.
            const temp = new ContourTexture(tex1.grid, overlap);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                temp,
                BooleanOperation.OR,
                src2
            );
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                temp,
                BooleanOperation.AND,
                src1
            );
            tex2.set(temp);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                tex2,
                BooleanOperation.OR,
                temp
            );
            temp.delete();
        } else {
            destination.generateTextureSlices(overlap);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                destination,
                BooleanOperation.OR,
                src1
            );
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                destination,
                BooleanOperation.AND,
                src2
            );
        }
        return true;
    }

    /**
     * Method to apply the {@link BooleanOperation.XOR XOR}-operation, which performs the {@link SDFOperations.OR or}-operation and subtracts the volume shared between {@link src1} and {@link src2} from that.\
     * {@link destination} will be scaled to cover the combined volume of {@link src1} and {@link src2} within the space defined by {@link destinationSpace}.\
     * Then both {@link src1} and {@link src2} will be rendered onto {@link destination} using the {@link SDFOperations.OR or}-operation.\
     * If {@link src1} and {@link src2} do overlap, their {@link BooleanOperation.AND AND} will be generated and subtracted from {@link destination}.\
     * @param src1 The first {@link ContourTexture} or {@link RegionOfInterest} that will be used within the {@link BooleanOperation.XOR XOR}-operation.
     * @param src2 The second {@link ContourTexture} or {@link RegionOfInterest} that will be used within the {@link BooleanOperation.XOR XOR}-operation.
     * @param destination {@link RegionOfInterest} that will be scaled to cover the volume of {@link src1} and {@link src2} within the space defined by {@link destinationSpace}.\
     * Any old {@link ContourTexture texture data} within {@link destination} will be overwritten.\
     * {@link destination} may be equal to {@link src1} or {@link src2}.
     * @param destinationSpace The {@link SliceSource space} with which the {@link destination} {@link RegionOfInterest} should align with.
     * {@link destinationSpace} may be equal to {@link src1} or {@link src2}.\
     * But preferably it should be the {@link SliceSource} that covers the most {@link SliceData slices} within the destination space.
     * @returns True if the operation was successful and destination was changed as a result, false otherwise.\
     * This will return false if both source textures are empty. Or if {@link destination} is equal to {@link src1} or {@link src2} and the other source is empty.
     */
    public static xor(
        src1: ContourTexture,
        src2: ContourTexture,
        destination: RegionOfInterest
    ): boolean {
        const tex1 = src1;
        const tex2 = src2;
        if (tex1) {
            if (tex2) {
                const rotation = destination._imageGrid.getOrientation();
                const overlap = RotatedBoundingBox.rotatedUnion(
                    rotation,
                    tex1.contentBoundingBox,
                    tex2.contentBoundingBox
                );
                let and: ContourTexture | null = null;
                if (overlap) {
                    //if they do, we must perform and, or and and_not
                    overlap.addMargins(
                        destination.texture
                            ? destination.texture.margin
                            : ContourTexture.DEFAULT_MARGIN
                    );
                    and = new ContourTexture(tex1.grid, overlap);
                    RenderStorage.getRenderer(
                        SDFOperationRenderer
                    ).renderOnDestination(and, BooleanOperation.OR, src1);
                    RenderStorage.getRenderer(
                        SDFOperationRenderer
                    ).renderOnDestination(and, BooleanOperation.AND, src2);
                }
                //we perform the regular or-operation which correctly handles different source, destination combinations, prepares destination and renders the or-operation.
                this.or(src1, src2, destination);
                if (and) {
                    RenderStorage.getRenderer(
                        SDFOperationRenderer
                    ).renderOnDestination(
                        destination,
                        BooleanOperation.AND_NOT,
                        and
                    );
                }
                return true;
            } else if (destination.texture === src1) {
                return false;
            } else {
                destination.generateTextureSlices(tex1.fullBoundingBox);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(destination, BooleanOperation.OR, src1);
                return true;
            }
        } else if (tex2) {
            if (destination.texture === src2) {
                return false;
            } else {
                destination.generateTextureSlices(tex2.fullBoundingBox);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(destination, BooleanOperation.OR, src2);
                return true;
            }
        } else {
            //TODO: Might want to clear destination as a result though?
            return false;
        }
    }

    /**
     * Method to apply the {@link BooleanOperation.AND_NOT AND_NOT}-operation, which subtracts {@link src2} from {@link src1}.
     * @throws Error if {@link src1} is an empty texture. As subtracting {@link src2} from empty texture does not make sense.
     * @param src1 {@link ContourTexture} or {@link RegionOfInterest} from which the {@link src2} will be subtracted.
     * @param src2 {@link ContourTexture} or {@link RegionOfInterest} that will be subtracted from the {@link src1}.
     * @param destination {@link RegionOfInterest} on which the result will be rendered.\
     * Any old {@link ContourTexture texture data} within {@link destination} will be overwritten.\
     * {@link destination} will be scaled to fill {@link src1} within the space defined by {@link destinationSpace}.\
     * {@link destination} may be equal to {@link src1} or {@link src2}.
     * @param destinationSpace {@link SliceSource} defining the space that the {@link destination} {@link RegionOfInterest} should align with.\
     * @returns True if the operation was successful and destination was changed as a result, false otherwise.\
     * This will return false if {@link src2} is an empty texture or does not overlap with {@link src1} and {@link destination} is equal to {@link src1}.\
     */
    public static andNot(
        src1: ContourTexture,
        src2: ContourTexture,
        destination: RegionOfInterest
    ) {
        const tex1 = src1;
        if (!tex1) {
            throw new Error(
                `Attempt to perform SDF boolean operation 'and_not', while src1 was empty texture`
            );
        }
        const tex2 = src2;
        if (!tex2) {
            if (destination.texture === src1) {
                return false;
            } else {
                this.or(src1, src2, destination);
                return;
            }
        }
        const rotation = destination._imageGrid.getOrientation();
        const overlap = RotatedBoundingBox.rotatedUnion(
            rotation,
            tex1.contentBoundingBox,
            tex2.contentBoundingBox
        );
        if (!overlap) {
            if (destination.texture === src1) {
                return;
            } else {
                destination.generateTextureSlices(tex1.fullBoundingBox);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(destination, BooleanOperation.OR, src1);
                return;
            }
        } else {
            overlap.addMargins(
                destination.texture
                    ? destination.texture.margin
                    : ContourTexture.DEFAULT_MARGIN
            );
            if (src1 === destination.texture) {
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(src1, BooleanOperation.AND_NOT, src2);
            } else if (src2 === destination.texture) {
                const temp = new ContourTexture(
                    tex1.grid,
                    tex1.fullBoundingBox
                );
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(temp, BooleanOperation.OR, src1);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(temp, BooleanOperation.AND_NOT, src2);
                tex2.set(temp);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(tex2, BooleanOperation.OR, temp);
                temp.delete();
            } else {
                destination.generateTextureSlices(tex1.fullBoundingBox);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(destination, BooleanOperation.OR, src1);
                RenderStorage.getRenderer(
                    SDFOperationRenderer
                ).renderOnDestination(
                    destination,
                    BooleanOperation.AND_NOT,
                    src2
                );
            }
        }
    }
}
